<template>
  <div class="receiving-type-navigation">
    <button
      v-if="options.length > 1"
      ref="previous"
      type="button"
      class="receiving-type-step"
      data-step="previous"
      :aria-label="$t('receivingReport.previousType')"
      :title="$t('receivingReport.previousType')"
      :disabled="loading || !hasPrevious"
      @click="$emit('step', -1)"
    ><feather-icon
      icon="ChevronLeftIcon"
      size="18"
      aria-hidden="true"
    /></button>
    <div
      ref="scroller"
      class="receiving-type-tabs"
      role="group"
      :aria-label="$t('receivingReport.chooseType')"
      @keydown="onKeydown"
    >
      <button
        v-for="option in options"
        :key="option.betType"
        ref="buttons"
        type="button"
        class="receiving-type-tab"
        :data-type="option.betType"
        :aria-pressed="value === option.betType ? 'true' : 'false'"
        :tabindex="value === option.betType ? 0 : -1"
        :disabled="loading"
        @click="$emit('input', option.betType)"
      >
        <span>{{ $t(option.label) }}</span>
        <small v-if="showCounts">({{ (option.numbers || []).length.toLocaleString('en-US') }})</small>
      </button>
    </div>
    <button
      v-if="options.length > 1"
      ref="next"
      type="button"
      class="receiving-type-step"
      data-step="next"
      :aria-label="$t('receivingReport.nextType')"
      :title="$t('receivingReport.nextType')"
      :disabled="loading || !hasNext"
      @click="$emit('step', 1)"
    ><feather-icon
      icon="ChevronRightIcon"
      size="18"
      aria-hidden="true"
    /></button>
  </div>
</template>

<script>
export default {
  props: {
    options: { type: Array, required: true },
    value: { type: Number, required: true },
    showCounts: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
  },
  computed: {
    optionKeys() { return this.options.map(option => option.betType).join(',') },
    activeIndex() { return this.options.findIndex(option => option.betType === this.value) },
    hasPrevious() { return this.activeIndex > 0 },
    hasNext() { return this.activeIndex >= 0 && this.activeIndex < this.options.length - 1 },
  },
  watch: {
    value() {
      const focused = document.activeElement
      const atDisabledEdge = (focused === this.$refs.previous && !this.hasPrevious) || (focused === this.$refs.next && !this.hasNext)
      this.revealActive(true)
      if (atDisabledEdge) {
        this.$nextTick(() => {
          const button = this.activeButton()
          if (button) button.focus({ preventScroll: true })
        })
      }
    },
    optionKeys() { this.revealActive() },
    showCounts() { this.revealActive() },
    '$i18n.locale': function localeChanged() { this.revealActive() },
  },
  mounted() { this.revealActive() },
  methods: {
    activeButton() { return this.$refs.buttons?.find(button => button.dataset.type === String(this.value)) },
    async revealActive(animate = false) {
      await this.$nextTick()
      const { scroller } = this.$refs
      const button = this.activeButton()
      if (!scroller || !button) return
      const box = scroller.getBoundingClientRect()
      const item = button.getBoundingClientRect()
      const offset = item.left < box.left + 6 ? item.left - box.left - 6 : Math.max(0, item.right - box.right + 6)
      if (!offset) return
      const smooth = animate && !this.loading && !window.matchMedia('(prefers-reduced-motion: reduce)').matches
      // Scroll only this strip; scrollIntoView would also move the report page.
      scroller.scrollTo({ left: scroller.scrollLeft + offset, behavior: smooth ? 'smooth' : 'instant' })
    },
    async onKeydown(event) {
      if (this.loading || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return
      const index = this.options.findIndex(option => String(option.betType) === event.target.dataset.type)
      if (index < 0) return
      event.preventDefault()
      let next = index + (event.key === 'ArrowRight' ? 1 : -1)
      if (event.key === 'Home') next = 0
      if (event.key === 'End') next = this.options.length - 1
      next = Math.max(0, Math.min(this.options.length - 1, next))
      this.$emit('input', this.options[next].betType)
      await this.$nextTick()
      const button = this.activeButton()
      if (button) button.focus({ preventScroll: true })
      this.revealActive(true)
    },
  },
}
</script>
